import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { Button, Tag, Typography, theme as antdTheme } from 'antd';
import {
  BankOutlined,
  CalendarOutlined,
  ContactsOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { space, tracking, typeScale } from '@viq-admin/ui';
import type { SearchGroup, SearchKind, SearchResult } from './searchModel';

/**
 * The header search's result list — the same list in the desktop dropdown and
 * the phone's full-screen search.
 *
 * ARIA combobox pattern: the input (owned by the caller) has
 * `role="combobox"` and points `aria-activedescendant` at the active option
 * here, so focus never leaves the input while arrow keys move the highlight.
 * Groups are `role="group"`s labelled by their heading. The "Recently opened"
 * heading and its Clear button sit just above the listbox rather than inside
 * it, because a listbox may only contain options and groups.
 */

const KIND_ICON: Record<SearchKind, ReactNode> = {
  company: <BankOutlined />,
  user: <UserOutlined />,
  conference: <CalendarOutlined />,
  lead: <ContactsOutlined />,
};

/** DOM id of the option at flat index `index` (see `optionId` in HeaderSearch). */
export type OptionIdFn = (index: number) => string;

export interface SearchResultsProps {
  groups: SearchGroup[];
  /** Flat index of the highlighted option, or -1. */
  activeIndex: number;
  listboxId: string;
  optionId: OptionIdFn;
  /** The trimmed query, for the "no matches" line. Empty = showing recents. */
  query: string;
  /** Users, conferences or archived tenants are still loading. */
  loading: boolean;
  onPick: (result: SearchResult) => void;
  onHover: (index: number) => void;
  onClearRecent: () => void;
}

export function SearchResults({
  groups,
  activeIndex,
  listboxId,
  optionId,
  query,
  loading,
  onPick,
  onHover,
  onClearRecent,
}: SearchResultsProps) {
  const { token } = antdTheme.useToken();

  // Keep the keyboard highlight in view in a long list. jsdom has no
  // scrollIntoView, hence the optional call.
  useEffect(() => {
    if (activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, optionId]);

  const heading: CSSProperties = {
    textTransform: 'uppercase',
    fontSize: typeScale.caption,
    letterSpacing: tracking.eyebrow,
    fontWeight: 600,
    color: token.colorTextSecondary,
    padding: `${space.sm}px ${space.sm}px ${space.xs}px`,
  };
  const hint: CSSProperties = {
    color: token.colorTextSecondary,
    padding: `${space.sm}px ${space.sm}px`,
  };

  const showingRecent = groups.length === 1 && groups[0].key === 'recent';
  const recentHeadingId = `${listboxId}-recent`;
  let index = -1;

  return (
    <div>
      {showingRecent && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div id={recentHeadingId} style={heading}>
            Recently opened
          </div>
          <Button
            type="link"
            size="small"
            onClick={onClearRecent}
            // Keep focus in the search input (the desktop dropdown closes on blur).
            onMouseDown={(e) => e.preventDefault()}
          >
            Clear
          </Button>
        </div>
      )}
      <div role="listbox" id={listboxId} aria-label="Search results">
        {groups.map((group) => {
          const headingId = group.key === 'recent' ? recentHeadingId : `${listboxId}-${group.key}`;
          return (
            <div key={group.key} role="group" aria-labelledby={headingId}>
              {group.key !== 'recent' && (
                <div id={headingId} role="presentation" style={heading}>
                  {group.title}
                </div>
              )}
              {group.results.map((result) => {
                index += 1;
                const i = index;
                const active = i === activeIndex;
                return (
                  <div
                    key={result.key}
                    id={optionId(i)}
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => onHover(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPick(result)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: space.sm,
                      padding: `${space.xs + 2}px ${space.sm}px`,
                      borderRadius: token.borderRadiusSM,
                      cursor: 'pointer',
                      background: active ? token.controlItemBgActive : undefined,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{ color: token.colorTextTertiary, lineHeight: `${token.lineHeight}em`, paddingTop: 1 }}
                    >
                      {KIND_ICON[result.kind]}
                    </span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, minWidth: 0 }}>
                        <Typography.Text strong ellipsis style={{ minWidth: 0 }}>
                          {result.label}
                        </Typography.Text>
                        {result.tag && (
                          <Tag style={{ marginInlineEnd: 0, flexShrink: 0 }}>{result.tag}</Tag>
                        )}
                      </span>
                      {result.description && (
                        <Typography.Text
                          type="secondary"
                          ellipsis
                          style={{ display: 'block', fontSize: token.fontSizeSM }}
                        >
                          {result.description}
                        </Typography.Text>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {!groups.length && !loading && (
        <div style={hint}>
          {query
            ? `No company, user or conference matches “${query}”.`
            : 'Type a name, email or id. Records you open show up here.'}
        </div>
      )}
      {loading && <div style={hint}>Loading users and conferences…</div>}
    </div>
  );
}
